/**
 * <s-product-image-zoom> — PDP image zoom preloader.
 *
 * Gallery buttons still open native dialogs declaratively via commandfor/show-modal.
 * This component only warms the selected zoom image on hover/focus so the
 * fullscreen dialog has the high-resolution image ready when opened.
 *
 * Required markup:
 *   [data-zoom-preload][commandfor] — gallery trigger button; commandfor points to a dialog id.
 *   dialog img[data-zoom-image]     — high-resolution image inside the target dialog.
 */
class SProductImageZoom extends HTMLElement {
  #ac;
  #preloaded = new Set();
  #preloadLinks = [];

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('pointerover', this.#handlePreloadIntent, opts);
    this.addEventListener('focusin', this.#handlePreloadIntent, opts);

    // The variant picker lives in a sibling column, so listen on the shared product detail wrapper.
    const productDetail = this.closest('[data-product-detail]');
    productDetail?.addEventListener('variant:media-change', this.#handleVariantMedia, opts);
  }

  #handleVariantMedia = (event) => {
    const mediaId = event.detail?.mediaId;
    const gallery = this.querySelector('[data-product-gallery]');
    if (!mediaId || !gallery) return;

    const item = gallery.querySelector(`:scope > [data-media-id="${CSS.escape(String(mediaId))}"]`);
    if (!item) return;

    const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (gallery.firstElementChild !== item) {
      const promote = () => {
        const previousLead = gallery.firstElementChild;
        gallery.prepend(item);
        // The lead tile is larger, so swap responsive sizes to keep both images sharp.
        this.#setTileSizes(item, gallery.dataset.leadSizes, 'eager');
        this.#setTileSizes(previousLead, gallery.dataset.thumbSizes);
      };

      if (smooth && document.startViewTransition) {
        // Name each tile so the browser morphs sizes and positions instead of cross-fading the page.
        const tiles = Array.from(gallery.children);
        tiles.forEach((tile, index) => { tile.style.viewTransitionName = `pdp-media-${index}`; });
        const vt = document.startViewTransition(promote);
        vt.finished.finally(() => {
          tiles.forEach((tile) => { tile.style.viewTransitionName = ''; });
        });
      } else {
        promote();
      }
    }

    gallery.scrollTo({ left: 0, behavior: smooth ? 'smooth' : 'auto' });

    // On desktop the gallery is a grid, so bring its top back into view if the shopper scrolled past it.
    // On mobile the picker sits below the gallery, so never pull the shopper away from it there.
    const isDesktop = window.matchMedia('(min-width: 768px)').matches;
    if (isDesktop && gallery.getBoundingClientRect().top < 0) {
      gallery.scrollIntoView({ block: 'start', behavior: smooth ? 'smooth' : 'auto' });
    }
  };

  #setTileSizes(tile, sizes, loading) {
    if (!tile || !sizes) return;
    for (const img of tile.querySelectorAll('img')) {
      img.sizes = sizes;
      if (loading) img.loading = loading;
    }
  }

  disconnectedCallback() {
    this.#ac?.abort();

    for (const link of this.#preloadLinks) {
      link.remove();
    }
    this.#preloadLinks = [];
    this.#preloaded.clear();
  }

  #handlePreloadIntent = (event) => {
    if (!(event.target instanceof Element)) return;

    const source = event.target.closest('[data-zoom-preload]');
    if (!source || !this.contains(source)) return;

    this.#preload(source);
  };

  #preload(source) {
    const dialogId = source.getAttribute('commandfor');
    if (!dialogId || this.#preloaded.has(dialogId)) return;

    const dialog = document.getElementById(dialogId);
    const image = dialog?.querySelector('img[data-zoom-image]');
    if (!(image instanceof HTMLImageElement)) return;

    this.#preloaded.add(dialogId);
    image.loading = 'eager';
    image.fetchPriority = 'high';

    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = image.src;
    if (image.srcset) link.setAttribute('imagesrcset', image.srcset);
    if (image.sizes) link.setAttribute('imagesizes', image.sizes);
    document.head.append(link);
    this.#preloadLinks.push(link);

    if (typeof image.decode === 'function') {
      image.decode().catch(() => {});
    }
  }
}

if (!customElements.get('s-product-image-zoom')) {
  customElements.define('s-product-image-zoom', SProductImageZoom);
}
